import { gsap } from "gsap";
import { Flip } from "gsap/Flip";

export default function initGallery() {

    gsap.registerPlugin(Flip);

    gsap.defaults({
        ease: "power4.inOut",
        duration: 0.8,
    });

    function createLightbox(container, {
        onStart,
        onOpen,
        onClose,
        onCloseComplete
    } = {}) {

        const elements = {
            wrapper: container.querySelector('[data-lightbox="wrapper"]'),
            triggers: container.querySelectorAll('[data-lightbox="trigger"]'),
            triggerParents: container.querySelectorAll('[data-lightbox="trigger-parent"]'),
            items: container.querySelectorAll('[data-lightbox="item"]'),
            nav: container.querySelectorAll('[data-lightbox="nav"]'),
            caption: container.querySelector('[data-lightbox="caption"]'),
            counter: {
                current: container.querySelector('[data-lightbox="counter-current"]'),
                total: container.querySelector('[data-lightbox="counter-total"]')
            },
            buttons: {
                prev: container.querySelector('[data-lightbox="prev"]'),
                next: container.querySelector('[data-lightbox="next"]'),
                close: container.querySelector('[data-lightbox="close"]')
            }
        };

        const { prev, next, close } = elements.buttons;
        const captionCol = elements.caption?.closest('.lightbox-nav__col');
        const mainTimeline = gsap.timeline();

        if (elements.counter.total) {
            elements.counter.total.textContent = elements.triggers.length;
        }

        function getActiveIndex() {
            return Array.from(elements.items).findIndex(item => item.classList.contains('is-active'));
        }

        function killAllTweens() {
            gsap.killTweensOf([
                elements.wrapper,
                elements.nav,
                elements.triggerParents,
                elements.items,
                container.querySelector('[data-lightbox="original"]')
            ]);
        }

        function markOriginal(trigger, img) {
            trigger.setAttribute('data-lightbox', 'original-parent');
            img.setAttribute('data-lightbox', 'original');
        }

        function unmarkOriginal() {
            const originalItem = container.querySelector('[data-lightbox="original"]');
            const originalParent = container.querySelector('[data-lightbox="original-parent"]');
            if (originalItem && originalParent) {
                gsap.set(originalItem, { clearProps: "all" });
                originalParent.appendChild(originalItem);
                originalParent.removeAttribute('data-lightbox');
                originalItem.removeAttribute('data-lightbox');
            }
        }

        function closeLightbox() {
            onClose?.();
            container.removeEventListener('click', handleOutsideClick);
            mainTimeline.clear();
            killAllTweens();

            elements.wrapper.classList.remove('is-active');
            elements.items.forEach(item => item.classList.remove('is-active'));
            unmarkOriginal();

            const tl = gsap.timeline({
                defaults: { ease: "power2.inOut" },
                // onComplete: () => {
                    // elements.wrapper.classList.remove('is-active');

                    // elements.items.forEach(item => {
                    //     item.classList.remove('is-active');
                    //     const img = item.querySelector('img');
                    //     if (img) img.style.display = '';
                    // });
                    //
                    // const originalParent = container.querySelector('[data-lightbox="original-parent"]');
                    // if (originalParent) {
                    //     originalParent.parentElement.style.removeProperty('height');
                    // }

                    // unmarkOriginal();

                    // onCloseComplete?.();
                // }
            });

            const activeItem = container.querySelector('[data-lightbox="item"].is-active');

            tl.to(elements.triggerParents, {
                autoAlpha: 1,
                duration: 0.5,
                stagger: 0.03,
                overwrite: true
            })
                .to(elements.nav, {
                    autoAlpha: 0,
                    y: "1rem",
                    duration: 0.4
                }, "<")
                .to(elements.wrapper, {
                    backgroundColor: "rgba(0,0,0,0)",
                    duration: 0.4
                }, "<")
                .to(activeItem, {
                    autoAlpha: 0,
                    duration: 0.4
                }, "<")
                .set([elements.items, activeItem, elements.triggerParents], { clearProps: "all" });

            mainTimeline.add(tl);
        }

        function handleOutsideClick(event) {
            if (event.detail === 0) return;
            const clickedElement = event.target;
            const isOutside = !clickedElement.closest('[data-lightbox="item"].is-active img, [data-lightbox="nav"], [data-lightbox="close"], [data-lightbox="trigger"]');
            if (isOutside) closeLightbox();
        }

        // Full-size images carry their URL in data-src and are only fetched when
        // needed, so the page load isn't flooded by every hero image at once.
        function loadItemImage(index) {
            const item = elements.items[index];
            if (!item) {
                return;
            }
            const img = item.querySelector('img.lightbox-img');
            if (img && !img.getAttribute('src') && img.dataset.src) {
                img.setAttribute('src', img.dataset.src);
            }
        }

        function loadWithNeighbours(index) {
            const total = elements.items.length;
            if (total === 0) {
                return;
            }

            const activeImg = elements.items[index]?.querySelector('img.lightbox-img');
            if (activeImg) {
                activeImg.setAttribute('fetchpriority', 'high');
            }

            loadItemImage(index);
            loadItemImage((index + 1) % total);
            loadItemImage((index - 1 + total) % total);
        }

        function setCaption(index) {
            const caption = elements.items[index].getAttribute('data-lightbox-caption');
            if (captionCol) {
                const img = elements.items[index].querySelector('img.lightbox-img');
                if (img) {
                    const applyWidth = () => { captionCol.style.width = `${img.width}px`; };
                    img.complete ? applyWidth() : img.addEventListener('load', applyWidth, { once: true });
                }
            }
            if (caption && elements.caption && caption !== '') {
                elements.caption.textContent = caption;
            } else {
                elements.caption.textContent = '';
            }
        }

        function updateActiveItem(index, afterFlip = false) {
            loadWithNeighbours(index);

            elements.items.forEach((item, i) => {
                item.classList.toggle('is-active', i === index);
            });

            afterFlip ? setTimeout(() => setCaption(index), 500) : setCaption(index);

            if (elements.counter.current) {
                elements.counter.current.textContent = index + 1;
            }
        }

        elements.triggers.forEach((trigger, index) => {
            trigger.addEventListener('click', () => {
                onStart?.();
                mainTimeline.clear();
                killAllTweens();

                const img = trigger.querySelector("img");
                const state = Flip.getState(img);
                const triggerRect = trigger.getBoundingClientRect();
                trigger.parentElement.style.height = `${triggerRect.height}px`;

                markOriginal(trigger, img);
                updateActiveItem(index, true);
                container.addEventListener('click', handleOutsideClick);

                const tl = gsap.timeline({ onComplete: () => onOpen?.() });
                elements.wrapper.classList.add('is-active');

                const targetItem = elements.items[index];
                // const lightboxImage = targetItem.querySelector('img');
                // if (lightboxImage) lightboxImage.style.display = 'none';

                elements.triggerParents.forEach(otherTrigger => {
                    if (otherTrigger !== trigger) {
                        gsap.to(otherTrigger, {
                            autoAlpha: 0,
                            duration: 0.4,
                            stagger: 0.02,
                            overwrite: true
                        });
                    }
                });

                if (!targetItem.contains(img)) {
                    targetItem.appendChild(img);

                    tl.add(
                        Flip.from(state, {
                            targets: img,
                            duration: 0.01,
                            ease: "power2.inOut"
                        }), 0
                    );
                }

                tl.to(elements.wrapper, {
                    backgroundColor: "rgba(0,0,0,0.75)",
                    duration: 0.6
                }, 0)
                    .fromTo(elements.nav, {
                        autoAlpha: 0,
                        y: "1rem"
                    }, {
                        autoAlpha: 1,
                        y: "0rem",
                        duration: 0.6,
                        stagger: { each: 0.05, from: "center" }
                    }, 0.2);

                mainTimeline.add(tl);
            });
        });

        next?.addEventListener('click', () => {
            const currentIndex = getActiveIndex();
            const nextIndex = (currentIndex + 1) % elements.items.length;
            updateActiveItem(nextIndex);
        });

        prev?.addEventListener('click', () => {
            const currentIndex = getActiveIndex();
            const prevIndex = (currentIndex - 1 + elements.items.length) % elements.items.length;
            updateActiveItem(prevIndex);
        });

        close?.addEventListener('click', closeLightbox);

        document.addEventListener('keydown', (event) => {
            if (!elements.wrapper.classList.contains('is-active')) return;
            switch (event.key) {
                case 'Escape': closeLightbox(); break;
                case 'ArrowRight': next?.click(); break;
                case 'ArrowLeft': prev?.click(); break;
            }
        });
    }

    document.querySelectorAll("[data-gallery]").forEach(createLightbox);
}
